// The AI agent account (ADR-034, 25 October 2026). Jacky: "we need to establish a AI Agent account that I
// authorize and to be used during build and troubleshooting issue".
//
// One card on Settings: who the account is, whether it is on, and the last sessions that borrowed it.
// Authorize is shown only to its owner; Disable to anyone on staff. Both are set_agent_account_status, and
// his click is the approval (ADR-027) — there is no second card.
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {Bot} from 'lucide-react';
import {Button, cn} from '@aiw/ui';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {day, time} from '../lib/format';
import {OUTCOME_TONE, useProposer} from '../hooks/slice-b';

interface AgentAccount {
  id: string; name: string; label: string; status: 'pending' | 'active' | 'disabled'; scope: string;
  authorized_at: string | null; disabled_at: string | null; disabled_by: string | null; status_note: string;
  can_authorize: boolean;
  recent: {id: string; started_at: string; purpose: string; session: string; paths: string[]}[];
}

const TONE: Record<AgentAccount['status'], string> = {
  active: 'border-success/40 bg-success/10 text-success',
  pending: 'border-warning/40 bg-warning/10 text-warning',
  disabled: 'border-border bg-muted text-muted-foreground',
};
const when = (iso: string | null) => (iso ? `${day(iso)} ${time(iso)}` : '—');

export function AgentAccountCard() {
  const {t} = useT();
  const queries = useQueryClient();
  const {send, outcomes, busy} = useProposer();
  /** GET /api/agent-accounts is a statement route (the typed chain is at TS's depth limit). */
  const accounts = useQuery({queryKey: ['agent-accounts'], staleTime: 15_000, queryFn: async () => {
    const r = await fetchImpl(`${env.apiBase}/api/agent-accounts`, {headers: await authHeaders()});
    const b = (await r.json()) as {data?: unknown};
    if (!r.ok || !Array.isArray(b.data)) throw new Error('Agent accounts unavailable');
    return b.data as AgentAccount[];
  }});
  if (!accounts.data?.length) return null;

  const set = async (a: AgentAccount, status: 'active' | 'disabled') => {
    await send(a.name, 'set_agent_account_status', {name: a.name, status, note: ''}, t(status === 'active' ? 'agentAccount.authorized' : 'agentAccount.disabled'));
    await queries.invalidateQueries({queryKey: ['agent-accounts']});
  };

  return (
    <section className="mb-4 space-y-2" aria-label={t('agentAccount.title')}>
      {accounts.data.map((a) => {
        const outcome = outcomes[a.name];
        return (
          <article key={a.id} className="rounded-lg border bg-card">
            <header className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
              <Bot className="h-4 w-4 text-muted-foreground" aria-hidden />
              <h2 className="font-display text-sm font-semibold">{t('agentAccount.title')} · {a.label}</h2>
              <span className={cn('rounded-full border px-2 py-0.5 text-micro font-medium', TONE[a.status])}>{t(`agentAccount.status.${a.status}`)}</span>
              <span className="text-micro text-muted-foreground">{t('agentAccount.scope')}</span>
            </header>
            <div className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">{t('agentAccount.what')}</p>
                <p className="text-micro text-muted-foreground">
                  {a.status === 'active' && t('agentAccount.since', {when: when(a.authorized_at)})}
                  {a.status === 'disabled' && t('agentAccount.disabledAt', {when: when(a.disabled_at)})}
                  {a.status === 'pending' && t('agentAccount.waiting')}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {a.status !== 'active' && a.can_authorize && <Button size="sm" disabled={busy[a.name]} onClick={() => void set(a, 'active')}>{t('agentAccount.authorize')}</Button>}
                  {a.status === 'active' && <Button size="sm" variant="outline" disabled={busy[a.name]} onClick={() => void set(a, 'disabled')}>{t('agentAccount.disable')}</Button>}
                  {a.status !== 'active' && !a.can_authorize && <span className="text-micro text-muted-foreground">{t('agentAccount.ownerOnly')}</span>}
                  {outcome && <span className={cn('text-micro', OUTCOME_TONE[outcome.tone])}>{outcome.text}</span>}
                </div>
              </div>
              <div>
                <h3 className="mb-1 text-micro font-medium uppercase tracking-wide text-muted-foreground">{t('agentAccount.recent')}</h3>
                {a.recent.length === 0
                  ? <p className="text-micro text-muted-foreground">{t('agentAccount.none')}</p>
                  : <ul className="space-y-1">{a.recent.map((u) => (
                      <li key={u.id} className="text-micro"><span className="text-muted-foreground">{when(u.started_at)}</span> · {u.purpose}{u.paths.length ? <span className="text-muted-foreground"> · {u.paths.slice(0, 3).join(', ')}{u.paths.length > 3 ? ' …' : ''}</span> : null}</li>
                    ))}</ul>}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}
